import React, { useEffect, useState } from 'react';
import { ChevronRight, User, ArrowUpRight } from 'lucide-react';
import PrimaryLogo from '../../../assets/fonts/Primary logo.png';
import { useNavigate } from 'react-router-dom';

export type SplitOption = 'select' | 'split' | 'fixed';

export interface MesaLandingPageProps {
  restaurantName: string;
  /** Optional URL for restaurant logo (shown in circle). Falls back to name. */
  restaurantLogoUrl?: string | null;
  mesaNumber: string;
  totalAmount: number;
  /** Manual mode: renders minimal Total/Pagado/Restante UI and routes to payment with custom amounts. */
  isManual?: boolean;
  montoTotalConImpuestos?: number;
  montoPagado?: number;
  restante?: number;
  mesaId?: string;
  restaurantId?: string;
  moneda?: string;
  onPayFull: () => void;
  /** Opens the Dividir cuenta bottom sheet (no navigation). */
  onSplitBill: () => void;
  /** Optional. When set, "Visita nuestra página" links here. */
  restaurantWebsiteUrl?: string | null;
  /** When true, show "Visita nuestra página" in the top header next to login (manual mode UX). */
  showWebsiteInHeader?: boolean;
  /** When true, show the Dividir cuenta bottom sheet overlay. */
  showSplitSheet?: boolean;
  /** Close the split sheet (e.g. Cancel). */
  onCloseSplitSheet?: () => void;
  /** User chose one of the three split options; mode is 'select' | 'split' | 'fixed'. */
  onSplitOption?: (mode: SplitOption) => void;
}

const MesaLandingPage: React.FC<MesaLandingPageProps> = ({
  restaurantName,
  restaurantLogoUrl,
  mesaNumber,
  totalAmount,
  isManual = false,
  montoPagado,
  restante,
  mesaId,
  restaurantId,
  moneda,
  onPayFull,
  onSplitBill,
  restaurantWebsiteUrl,
  showWebsiteInHeader = false,
  showSplitSheet = false,
  onCloseSplitSheet,
  onSplitOption,
}) => {
  const hasWebsite = Boolean(restaurantWebsiteUrl?.trim());
  const navigate = useNavigate();
  const [isClosing, setIsClosing] = useState(false);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const remainingNumber = Number(restante ?? 0);
  const paidNumber = Number(montoPagado ?? 0);
  const totalForManualLabel = Number(totalAmount ?? 0);

  const handleCloseSheet = () => {
    if (isClosing) return;
    setIsClosing(true);
  };

  const handleSheetPanelAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === 'sheet-slide-down' && isClosing) {
      setIsClosing(false); // reset so next time we open the sheet it doesn’t immediately play close animation
      onCloseSplitSheet?.();
    }
  };

  // Manual mode split sheet flow (no items in manual mode)
  const [manualSplitView, setManualSplitView] = useState<'options' | 'fixed'>('options');
  const [manualFixedAmountDraft, setManualFixedAmountDraft] = useState<number>(0);

  useEffect(() => {
    if (showSplitSheet && isManual) {
      setManualSplitView('options');
      setManualFixedAmountDraft(remainingNumber);
    }
  }, [showSplitSheet, isManual, remainingNumber]);

  const handleCloseSheetNow = () => {
    setManualSplitView('options');
    setManualFixedAmountDraft(0);
    onCloseSplitSheet?.();
  };

  const handleManualPayFixed = () => {
    const amount = manualFixedAmountDraft;
    if (!(isFinite(amount) && amount > 0)) return;
    if (amount > remainingNumber + 0.01) return;
    if (!mesaId || !restaurantId) return;

    handleCloseSheetNow();
    navigate('/tip-selection', {
      state: {
        mesaId,
        restaurantId,
        waiterName: 'tu mesero',
        staffId: undefined,
        amount,
        moneda: moneda ?? 'DOP',
      },
    });
  };

  return (
    <div className="flex flex-col min-h-screen bg-white animate-in fade-in slide-in-from-bottom-4 duration-300">
      {/* Header: big YAP logo left, login right */}
      <div className="flex justify-between items-center px-6 pt-6 pb-2">
        <div className="flex items-center">
          <img
            src={PrimaryLogo}
            alt="YAP"
            className="h-14 w-auto object-contain"
          />
        </div>
        <div className="flex items-center gap-4">
          {showWebsiteInHeader && (
            <a
              href={hasWebsite ? restaurantWebsiteUrl! : '#'}
              className="flex items-center gap-1 text-[#8E8E93] text-[11px] font-bold hover:text-gray-700"
              onClick={(e) => !hasWebsite && e.preventDefault()}
            >
              <ArrowUpRight size={12} strokeWidth={2} />
              <span>Visita nuestra página</span>
            </a>
          )}
          <a
            href="#"
            className="flex items-center gap-1.5 text-[#8E8E93] text-[13px] font-medium"
            onClick={(e) => e.preventDefault()}
          >
            <User size={18} strokeWidth={1.5} />
            <span>login</span>
          </a>
        </div>
      </div>

      {/* Main Content - restaurant image moved up */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-2 pb-4">
        {/* Manual mode reorder: link + mesa on top of logo */}
        {isManual && !showWebsiteInHeader && (
          <>
            <a
              href={hasWebsite ? restaurantWebsiteUrl! : '#'}
              className="flex items-center gap-1 text-[#8E8E93] text-[11px] font-bold mb-3 hover:text-gray-700"
              onClick={(e) => !hasWebsite && e.preventDefault()}
            >
              <ArrowUpRight size={12} strokeWidth={2} />
              <span>Visita nuestra página</span>
            </a>
            <p className="text-[#8E8E93] text-[13px] font-medium mb-6 lowercase">mesa {mesaNumber}</p>
          </>
        )}

        {/* Restaurant Logo / Name - no pink when logo image is shown */}
        <div className={`w-48 h-48 rounded-full flex items-center justify-center mb-4 overflow-hidden ${restaurantLogoUrl ? 'bg-white' : 'bg-[#F5E6E8]'}`}>
          {restaurantLogoUrl ? (
            <img
              src={restaurantLogoUrl}
              alt={restaurantName}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-3xl font-serif tracking-widest text-gray-800">
              {restaurantName.toUpperCase()}
            </span>
          )}
        </div>

        {/* Total de la cuenta (manual mode gap) */}
        {isManual && (
          <div className="text-center mt-6 mb-6">
            <p className="text-sm text-gray-500 mb-2">Total de la cuenta</p>
            <h2 className="text-3xl font-bold text-black">RD${formatCurrency(totalForManualLabel)}</h2>
          </div>
        )}

        {/* Pagado / Restante box (manual mode) */}
        {isManual && (
          <div className="w-full bg-white border border-gray-200 rounded-none p-5 mb-8 shadow-sm shadow-gray-100/50 max-w-sm mx-auto">
            <div className="flex justify-between items-center text-sm mb-3.5">
              <span className="text-gray-500 font-medium">Pagado</span>
              <span className="font-semibold text-gray-900">RD${formatCurrency(paidNumber)}</span>
            </div>
            <div className="h-px w-full bg-gray-100 mb-3.5"></div>
            <div className="flex justify-between items-center">
              <span className="text-gray-900 font-semibold">Restante</span>
              <span className="text-lg font-bold text-gray-900">RD${formatCurrency(remainingNumber)}</span>
            </div>
          </div>
        )}

        {/* Visita nuestra página (default layout) */}
        {!isManual && !showWebsiteInHeader && (
          <a
            href={hasWebsite ? restaurantWebsiteUrl! : '#'}
            className="flex items-center gap-1 text-[#8E8E93] text-[11px] font-bold mb-3 hover:text-gray-700"
            onClick={(e) => !hasWebsite && e.preventDefault()}
          >
            <ArrowUpRight size={12} strokeWidth={2} />
            <span>Visita nuestra página</span>
          </a>
        )}

        {!isManual && (
          <p className="text-[#8E8E93] text-[13px] font-medium mb-6 lowercase">mesa {mesaNumber}</p>
        )}

        {/* Action Buttons - stay black on focus/active to avoid sticky "preselected" look on touch */}
        <div className="w-full max-w-sm space-y-4">
          <button
            type="button"
            onClick={onPayFull}
            className="landing-pay-btn w-full bg-black text-white py-5 px-6 flex justify-between items-center active:scale-[0.98] transition-all focus:bg-black active:bg-black hover:bg-gray-800"
          >
            <span className="font-medium text-[15px]">
              Pagar cuenta completa
            </span>
            <ChevronRight size={20} className="text-white flex-shrink-0" />
          </button>

          <button
            type="button"
            onClick={(e) => {
              (e.currentTarget as HTMLButtonElement).blur();
              onSplitBill();
            }}
            className="landing-pay-btn w-full bg-black text-white py-5 px-6 flex justify-between items-center active:scale-[0.98] transition-all focus:bg-black active:bg-black hover:bg-gray-800"
          >
            <span className="font-medium text-[15px]">Dividir cuenta</span>
            <ChevronRight size={20} className="text-white flex-shrink-0" />
          </button>

          {/* Legal + secure payment text and card logos below buttons */}
          <div className="w-full max-w-sm mt-6 pt-4 space-y-3 text-center">
            <p className="text-[11px] text-gray-500 leading-snug">
              Al realizar esta compra usted acepta los{' '}
              <a href="#" className="font-semibold text-gray-700 underline underline-offset-1" onClick={(e) => e.preventDefault()}>Términos de uso</a>
              {' '}y las{' '}
              <a href="#" className="font-semibold text-gray-700 underline underline-offset-1" onClick={(e) => e.preventDefault()}>Políticas de devolución</a>.
            </p>
            <p className="text-[11px] text-gray-500">
              Pagos procesados de forma segura.
            </p>
            <div className="flex items-center justify-center gap-4">
              <img src="/images/payment-logos/visa-logo-png-extra-large-size.png" alt="Visa" className="h-6 w-auto object-contain max-w-[48px]" />
              <img src="/images/payment-logos/Mastercard-logo.svg.png" alt="Mastercard" className="h-6 w-auto object-contain max-w-[48px]" />
            </div>
          </div>
        </div>
      </div>

      {/* Dividir cuenta — bottom sheet (slides up / slides down on close) */}
      {showSplitSheet && (
        <>
          <style>{`
            @keyframes sheet-backdrop-in {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes sheet-backdrop-out {
              from { opacity: 1; }
              to { opacity: 0; }
            }
            @keyframes sheet-slide-up {
              from { transform: translateY(100%); }
              to { transform: translateY(0); }
            }
            @keyframes sheet-slide-down {
              from { transform: translateY(0); }
              to { transform: translateY(100%); }
            }
            .sheet-backdrop {
              animation: sheet-backdrop-in 0.25s ease-out forwards;
            }
            .sheet-backdrop.sheet-closing {
              animation: sheet-backdrop-out 0.28s ease-in forwards;
            }
            .sheet-panel {
              animation: sheet-slide-up 0.35s cubic-bezier(0.32, 0.72, 0, 1) forwards;
            }
            .sheet-panel.sheet-closing {
              animation: sheet-slide-down 0.28s cubic-bezier(0.4, 0, 0.6, 1) forwards;
            }
          `}</style>
          <div
            className={`sheet-backdrop fixed inset-0 bg-black/40 z-40 ${isClosing ? 'sheet-closing' : ''}`}
            onClick={handleCloseSheet}
            aria-hidden
          />
          <div
            className={`sheet-panel fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl shadow-[0_-4px_24px_rgba(0,0,0,0.12)] ${isClosing ? 'sheet-closing' : ''}`}
            style={{ maxHeight: '70vh' }}
            onAnimationEnd={handleSheetPanelAnimationEnd}
          >
            <div className="flex flex-col pt-2 pb-8 px-5">
              {/* Handle bar */}
              <div className="w-10 h-1 rounded-full bg-gray-300 mx-auto mb-4" />
              {/* Header: title left, cancelar far right; less space below */}
              <div className="flex items-start justify-between gap-4 mb-2">
                <h2 className="text-[22px] sm:text-2xl font-bold text-gray-900 tracking-tight leading-tight flex-1">
                  Dividir cuenta
                </h2>
                <button
                  type="button"
                  onClick={handleCloseSheet}
                  className="text-[11px] text-gray-500 hover:text-gray-700 active:opacity-80 lowercase shrink-0"
                >
                  cancelar
                </button>
              </div>
              {isManual ? (
                <>
                  {manualSplitView === 'options' && (
                    <div className="flex flex-col gap-3 pl-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          (e.currentTarget as HTMLButtonElement).blur();
                          onSplitOption?.('split');
                        }}
                        className="landing-pay-btn w-full bg-black text-white py-4 px-5 flex justify-between items-center active:scale-[0.98] transition-all focus:bg-black active:bg-black hover:bg-gray-800"
                      >
                        <span className="font-medium text-[15px] lowercase">dividir equitativamente</span>
                        <ChevronRight size={20} className="text-white flex-shrink-0" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          (e.currentTarget as HTMLButtonElement).blur();
                          onSplitOption?.('fixed');
                        }}
                        className="landing-pay-btn w-full bg-black text-white py-4 px-5 flex justify-between items-center active:scale-[0.98] transition-all focus:bg-black active:bg-black hover:bg-gray-800"
                      >
                        <span className="font-medium text-[15px] lowercase">paga una cantidad fija</span>
                        <ChevronRight size={20} className="text-white flex-shrink-0" />
                      </button>
                    </div>
                  )}

                  {manualSplitView === 'fixed' && (
                    <div className="w-full mt-1">
                      <p className="text-sm text-gray-600 mb-3">¿Monto a pagar?</p>

                      <div className="flex items-center justify-center gap-4 mb-5">
                        <button
                          type="button"
                          onClick={() =>
                            setManualFixedAmountDraft((v) =>
                              Math.max(0.01, Math.round((v - 1) * 100) / 100)
                            )
                          }
                          className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center text-2xl text-gray-500 active:scale-[0.98]"
                        >
                          −
                        </button>

                        <span className="text-4xl font-semibold w-16 text-center text-gray-900">
                          {formatCurrency(manualFixedAmountDraft)}
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            setManualFixedAmountDraft((v) =>
                              Math.min(remainingNumber, Math.round((v + 1) * 100) / 100)
                            )
                          }
                          className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center text-2xl text-gray-500 active:scale-[0.98]"
                        >
                          +
                        </button>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-4 mb-5">
                        <p className="text-sm text-gray-600 mb-1">Restante</p>
                        <p className="text-3xl font-semibold text-gray-900">
                          RD${formatCurrency(remainingNumber)}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={handleManualPayFixed}
                        disabled={remainingNumber <= 0}
                        className="landing-pay-btn w-full bg-black text-white py-4 px-5 flex justify-between items-center active:scale-[0.98] transition-all focus:bg-black active:bg-black hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <span className="font-medium text-[15px] lowercase">
                          Pagar RD${formatCurrency(manualFixedAmountDraft)}
                        </span>
                        <ChevronRight size={20} className="text-white flex-shrink-0" />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col gap-3 pl-0">
                  <button
                    type="button"
                    onClick={(e) => {
                      (e.currentTarget as HTMLButtonElement).blur();
                      onSplitOption?.('select');
                    }}
                    className="landing-pay-btn w-full bg-black text-white py-4 px-5 flex justify-between items-center active:scale-[0.98] transition-all focus:bg-black active:bg-black hover:bg-gray-800"
                  >
                    <span className="font-medium text-[15px] lowercase">paga por tus items</span>
                    <ChevronRight size={20} className="text-white flex-shrink-0" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      (e.currentTarget as HTMLButtonElement).blur();
                      onSplitOption?.('split');
                    }}
                    className="landing-pay-btn w-full bg-black text-white py-4 px-5 flex justify-between items-center active:scale-[0.98] transition-all focus:bg-black active:bg-black hover:bg-gray-800"
                  >
                    <span className="font-medium text-[15px] lowercase">dividir equitativamente</span>
                    <ChevronRight size={20} className="text-white flex-shrink-0" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      (e.currentTarget as HTMLButtonElement).blur();
                      onSplitOption?.('fixed');
                    }}
                    className="landing-pay-btn w-full bg-black text-white py-4 px-5 flex justify-between items-center active:scale-[0.98] transition-all focus:bg-black active:bg-black hover:bg-gray-800"
                  >
                    <span className="font-medium text-[15px] lowercase">paga una cantidad fija</span>
                    <ChevronRight size={20} className="text-white flex-shrink-0" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MesaLandingPage;
